import { numberHelpers, zodSchemes } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { isFieldMandatory } from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { useNetSuiteVendorFieldSettings } from 'shared/hooks';
import { z } from 'zod';

import { isVendorWebAddressValid } from '../../helpers';
import { messages } from './NetSuiteVendorCreationPopupContent.messages';
import { VendorType } from './NetSuiteVendorCreationPopupContent.types';

const getTextFieldSchema = (fieldState: domain.DocumentFieldState) => {
    return isFieldMandatory(fieldState) ? zodSchemes.required.textField.string() : zodSchemes.optional.textField.string;
};

const getSelectFieldSchema = (fieldState: domain.DocumentFieldState) => {
    return isFieldMandatory(fieldState) ? zodSchemes.required.select.string() : zodSchemes.optional.select.string;
};

export const subsidiaryItem = z.object({
    subsidiaryId: zodSchemes.optional.select.string,
    creditLimit: zodSchemes.optional.numberField.number,
    taxCodeId: zodSchemes.optional.select.string,
});

export const getAddressLinesItem = (fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>) =>
    z
        .object({
            defaultBilling: z.boolean().optional(),
            defaultShipping: z.boolean().optional(),
            label: zodSchemes.optional.textField.string,
            countryId: zodSchemes.optional.select.string,
            attention: zodSchemes.optional.textField.string,
            addressee: zodSchemes.optional.textField.string,
            phone: zodSchemes.optional.textField.string,
            primaryAddress: zodSchemes.optional.textField.string,
            secondaryAddress: zodSchemes.optional.textField.string,
            state: zodSchemes.optional.textField.string,
            city: zodSchemes.optional.textField.string,
            zip: zodSchemes.optional.textField.string,
        })
        .superRefine((values, ctx) => {
            const isEmpty = Object.values(values).every((value) => typeof value === 'boolean' || !value);

            if (isEmpty) {
                return;
            }

            const fieldsForCheck: Array<{ state: domain.DocumentFieldState; path: keyof typeof values }> = [
                {
                    state: fieldSettings.addressLinesCountry,
                    path: 'countryId',
                },
                {
                    state: fieldSettings.addressLinesAttention,
                    path: 'attention',
                },
                {
                    state: fieldSettings.addressLinesAddressee,
                    path: 'addressee',
                },
                {
                    state: fieldSettings.addressLinesPhone,
                    path: 'phone',
                },
                {
                    state: fieldSettings.addressLinesPrimaryAddress,
                    path: 'primaryAddress',
                },
                {
                    state: fieldSettings.addressLinesSecondaryAddress,
                    path: 'secondaryAddress',
                },
                {
                    state: fieldSettings.addressLinesState,
                    path: 'state',
                },
                {
                    state: fieldSettings.addressLinesCity,
                    path: 'city',
                },
                {
                    state: fieldSettings.addressLinesZip,
                    path: 'zip',
                },
            ];

            for (const field of fieldsForCheck) {
                if (isFieldMandatory(field.state) && !values[field.path]) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '',
                        path: [field.path],
                    });
                }
            }
        });

const createCommonInformationSchema = (fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>) => {
    return z.object({
        categoryId: getSelectFieldSchema(fieldSettings.category),
        comments: getTextFieldSchema(fieldSettings.comments),
    });
};

const createIndividualInformationSchema = (fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>) => {
    return z
        .object({
            vendorType: z.literal(VendorType.Individual),
            companyName: zodSchemes.optional.textField.string,
            salutation: getTextFieldSchema(fieldSettings.salutation),
            firstName: zodSchemes.required.textField.string(),
            middleName: zodSchemes.optional.textField.string,
            lastName: zodSchemes.required.textField.string(),
            jobTitle: getTextFieldSchema(fieldSettings.jobTitle),
        })
        .merge(createCommonInformationSchema(fieldSettings));
};

const createCompanyInformationSchema = (fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>) => {
    return z
        .object({
            vendorType: z.literal(VendorType.Company),
            companyName: zodSchemes.required.textField.string(),
            webAddress: getTextFieldSchema(fieldSettings.webAddress).refine(
                (value) => isVendorWebAddressValid(value),
                messages.invalidUrl
            ),
        })
        .merge(createCommonInformationSchema(fieldSettings));
};

export const createSchema = (
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>,
    withTaxNumber: boolean
) => {
    return z
        .discriminatedUnion('vendorType', [
            createOtherSchema(fieldSettings, withTaxNumber).merge(createCompanyInformationSchema(fieldSettings)),
            createOtherSchema(fieldSettings, withTaxNumber).merge(createIndividualInformationSchema(fieldSettings)),
        ])
        .superRefine((data, ctx) => {
            const primarySubsidiaryIndex = data.subsidiaries?.findIndex(
                (subsidiary) => subsidiary.subsidiaryId === data.primarySubsidiaryId
            );
            const primarySubsidiary =
                primarySubsidiaryIndex !== undefined ? data.subsidiaries?.[primarySubsidiaryIndex] : null;

            if (
                isFieldMandatory(fieldSettings.creditLimit) &&
                !numberHelpers.isNumber(data.creditLimit) &&
                !numberHelpers.isNumber(primarySubsidiary?.creditLimit)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: messages.requiredField,
                    path: ['creditLimit'],
                });
            }

            if (isFieldMandatory(fieldSettings.taxCode) && !primarySubsidiary?.taxCodeId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: primarySubsidiary ? messages.requiredField : messages.requiredPrimarySubsidiaryTaxCode,
                    path: ['subsidiaries', ''],
                });

                if (primarySubsidiaryIndex !== undefined && primarySubsidiaryIndex !== -1) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '',
                        path: ['subsidiaries', primarySubsidiaryIndex, 'taxCodeId'],
                    });
                }
            }
        });
};

const createOtherSchema = (
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>,
    withTaxNumber: boolean
) => {
    return z.object({
        email: isFieldMandatory(fieldSettings.email)
            ? zodSchemes.required.textField.string().email()
            : zodSchemes.optional.textField.email(),
        phone: getTextFieldSchema(fieldSettings.phone),
        altPhone: getTextFieldSchema(fieldSettings.altPhone),
        fax: getTextFieldSchema(fieldSettings.fax),
        legalName: getTextFieldSchema(fieldSettings.legalName),
        accountNumber: getTextFieldSchema(fieldSettings.accountNumber),
        defaultExpenseAccountId: getSelectFieldSchema(fieldSettings.defaultExpenseAccount),
        defaultPayablesAccountId: getSelectFieldSchema(fieldSettings.defaultPayablesAccount),
        termsId: getSelectFieldSchema(fieldSettings.terms),
        creditLimit: zodSchemes.optional.numberField.number,
        incotermName: getSelectFieldSchema(fieldSettings.incoterm),
        taxNumber:
            withTaxNumber && isFieldMandatory(fieldSettings.taxNumber)
                ? zodSchemes.required.textField.string()
                : zodSchemes.optional.textField.string,
        is1099Eligible: z.boolean().optional(),
        isJobResource: z.boolean().optional(),

        primarySubsidiaryId: zodSchemes.required.select.string(),
        subsidiaries: z
            .array(
                subsidiaryItem.refine(
                    (data) =>
                        typeof data.subsidiaryId === 'string' ||
                        (typeof data.creditLimit !== 'number' && typeof data.taxCodeId !== 'string'),
                    {
                        message: '',
                        path: ['subsidiaryId'],
                    }
                )
            )
            .optional()
            .superRefine((subsidiaries, ctx) => {
                if (!subsidiaries) {
                    return;
                }

                const subsidiaryIndexMap = new Map<string, number>();

                for (let index = 0; index < subsidiaries.length; index++) {
                    const subsidiary = subsidiaries[index];

                    if (!subsidiary.subsidiaryId) {
                        continue;
                    }

                    const duplicatedSubsidiaryIndex = subsidiaryIndexMap.get(subsidiary.subsidiaryId);

                    if (duplicatedSubsidiaryIndex === undefined) {
                        subsidiaryIndexMap.set(subsidiary.subsidiaryId, index);

                        continue;
                    }

                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.duplicatedSubsidiary,
                        path: [''],
                    });

                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '',
                        path: [duplicatedSubsidiaryIndex, 'subsidiaryId'],
                    });

                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '',
                        path: [index, 'subsidiaryId'],
                    });
                }
            }),

        currencyId: zodSchemes.required.select.string(),
        currenciesIds: z.array(z.string()).optional(),

        addressLines: z.array(getAddressLinesItem(fieldSettings)).optional(),
    });
};
